import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../constants';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Button } from '../../components/library';
import HoverableImageThumbnail from '../catalog/productImages/HoverableImageThumbnail';
import { FormatDateString } from '../../utils/helper';

export const FULFILLMENT_ORDER_DETAIL_ROUTE = '/fulfillment/orders/:shopifyOrderId';

type ItemType = 'bike' | 'frame' | 'wheel' | 'paa' | 'unknown';
type WaveStatus = 'no_wave' | 'in_wave' | 'exception';

interface BinLocation {
  binCode: string;
  quantity: number;
  zone: string;
  parentZone: string | null;
}

interface LineItem {
  shopifyLineItemGid: string;
  sku: string | null;
  title: string;
  quantity: number;
  itemType: ItemType;
  imageUrl: string | null;
  binLocations: BinLocation[];
  upc: string | null;
  mpn: string | null;
  hasException?: boolean;
}

interface OrderDetail {
  shopifyOrderId: number;
  shopifyOrderGid: string;
  shopifyOrderName: string;
  createdAt: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  shippingAddress: {
    address1: string;
    address2: string | null;
    city: string;
    province: string;
    zip: string;
    country: string;
  } | null;
  waveStatus: WaveStatus;
  waveId: number | null;
  lifecycleStatus: string | null;
  lineItems: LineItem[];
  hasUnresolvedException: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ZoneGroup {
  zoneLabel: string; // e.g. "Warehouse › Finished Goods" or just "Finished Goods"
  bins: { binCode: string; quantity: number }[];
}

function groupBinsByZone(binLocations: BinLocation[]): ZoneGroup[] {
  const map = new Map<string, ZoneGroup>();
  for (const bin of binLocations) {
    const zoneLabel = bin.parentZone ? `${bin.parentZone} › ${bin.zone}` : bin.zone;
    if (!map.has(zoneLabel)) {
      map.set(zoneLabel, { zoneLabel, bins: [] });
    }
    const group = map.get(zoneLabel);
    if (group) group.bins.push({ binCode: bin.binCode, quantity: bin.quantity });
  }
  return Array.from(map.values());
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const WaveStatusChip: React.FC<{ status: WaveStatus; waveId: number | null }> = ({
  status,
  waveId,
}) => {
  if (status === 'exception') return <Chip label="Exception" size="small" color="error" />;
  if (status === 'in_wave') return <Chip label={`Wave #${waveId}`} size="small" color="primary" />;
  return <Chip label="No wave" size="small" />;
};

const GRID_COLUMNS = '60px 1fr auto minmax(0, 260px)';
const MAX_WIDTH = '820px';

// ─── Component ───────────────────────────────────────────────────────────────

const FulfillmentOrderDetail: React.FC = () => {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const history = useHistory();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/fulfillment/orders/${shopifyOrderId}`);
        setOrder((response as AxiosResponse<OrderDetail>).data);
      } catch {
        setError('Failed to load order details.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [shopifyOrderId]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
        <Box padding="4rem">
          <Alert severity="error">{error ?? 'Order not found.'}</Alert>
        </Box>
      </Box>
    );
  }

  const customerName = order.customer
    ? `${order.customer.firstName} ${order.customer.lastName}`.trim()
    : null;

  const addressParts = order.shippingAddress
    ? [
        order.shippingAddress.address1,
        order.shippingAddress.address2,
        `${order.shippingAddress.city}, ${order.shippingAddress.province} ${order.shippingAddress.zip}`,
      ].filter(Boolean)
    : [];

  return (
    <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
      {/* Back bar */}
      <Box sx={{ padding: '1.5rem 4rem 0' }}>
        <Button
          ordinality="secondary"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/fulfillment/orders')}
        >
          Orders
        </Button>
      </Box>

      {/* Two-column body */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '280px 1fr',
          minHeight: 0,
          flex: 1,
        }}
      >
        {/* ── Left: order header panel ── */}
        <Box
          sx={{
            borderRight: '1px solid',
            borderColor: 'grey.300',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Typography sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '1px' }}>
            {order.shopifyOrderName}
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={0.5}>
            <WaveStatusChip status={order.waveStatus} waveId={order.waveId} />
            {order.lifecycleStatus && <Chip label={order.lifecycleStatus} size="small" />}
          </Box>

          <Box
            component="hr"
            sx={{ border: 'none', borderTop: '1px solid', borderColor: 'grey.200', m: 0 }}
          />

          {customerName && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'grey.600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Customer
              </Typography>
              <Typography sx={{ fontSize: 14 }}>{customerName}</Typography>
              {order.customer?.email && (
                <Typography sx={{ fontSize: 14 }}>{order.customer.email}</Typography>
              )}
            </Box>
          )}

          {addressParts.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'grey.600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Ship to
              </Typography>
              {addressParts.map((line, i) => (
                <Typography key={i} sx={{ fontSize: 14 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: 'grey.600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Order date
            </Typography>
            <Typography sx={{ fontSize: 14 }}>{FormatDateString(order.createdAt)}</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: 'grey.600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Items
            </Typography>
            <Typography sx={{ fontSize: 14 }}>{order.lineItems.length}</Typography>
          </Box>
        </Box>

        {/* ── Right: line items ── */}
        <Box sx={{ p: '3rem 4rem' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 3 }}>Line Items</Typography>

          {/* Header row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              gap: 2,
              pb: 1,
              borderBottom: '2px solid',
              borderColor: 'grey.300',
              maxWidth: MAX_WIDTH,
              mb: 0.5,
            }}
          >
            <div />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Details
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Order Qty
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Location
            </Typography>
          </Box>

          {/* Line item rows */}
          {order.lineItems.map(item => {
            const zoneGroups = groupBinsByZone(item.binLocations);
            return (
              <Box
                key={item.shopifyLineItemGid}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLUMNS,
                  gap: 2,
                  alignItems: 'start',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'grey.200',
                  maxWidth: MAX_WIDTH,
                }}
              >
                {/* Thumbnail */}
                <HoverableImageThumbnail
                  src={item.imageUrl ?? undefined}
                  identifier={item.sku ?? undefined}
                />

                {/* Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.5px' }}>
                    {item.sku ?? '—'}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: 'grey.600' }}>{item.title}</Typography>
                  {item.upc && (
                    <Typography sx={{ fontSize: 11, mt: 0.25 }}>
                      <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
                        UPC
                      </Box>
                      <Box component="span" sx={{ color: 'grey.500' }}>
                        {item.upc}
                      </Box>
                    </Typography>
                  )}
                  {item.mpn && (
                    <Typography sx={{ fontSize: 11 }}>
                      <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
                        MPN
                      </Box>
                      <Box component="span" sx={{ color: 'grey.500' }}>
                        {item.mpn}
                      </Box>
                    </Typography>
                  )}
                  {item.hasException && (
                    <Box mt={0.5}>
                      <Chip label="Exception" size="small" color="error" />
                    </Box>
                  )}
                </Box>

                {/* Order Qty */}
                <Typography sx={{ fontSize: 14, color: 'grey.700', whiteSpace: 'nowrap' }}>
                  ×{item.quantity}
                </Typography>

                {/* Location — grouped by zone */}
                <Box>
                  {zoneGroups.length > 0 ? (
                    zoneGroups.map(group => (
                      <Box key={group.zoneLabel} sx={{ mb: group.bins.length > 0 ? 1 : 0 }}>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 700, color: 'grey.600', mb: 0.25 }}
                        >
                          {group.zoneLabel}
                        </Typography>
                        {group.bins.map(bin => (
                          <Box
                            key={bin.binCode}
                            sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', pl: 1 }}
                          >
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.800' }}>
                              {bin.binCode}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'grey.500' }}>
                              qty {bin.quantity}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ fontSize: 13, color: 'grey.500' }}>—</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FulfillmentOrderDetail;
